'use client';

import React from 'react';
import { buildTripCountryLinkWithMultipleFilters } from '@/lib/affiliates/builders/trip';
import { FaHotel, FaHome, FaBed } from 'react-icons/fa';
import { MdVilla, MdPool, MdApartment } from 'react-icons/md';
import { PiPlant } from 'react-icons/pi';
import { BsBuilding } from 'react-icons/bs';
import { renderTablerIcon } from '@/utils/iconRenderer';
import AffiliateLink from '@/components/common/AffiliateLink';
import { t } from '@/i18n/translations';

// "Gallery · Field" accommodation-type cards. The Trip.com affiliate href and
// AffiliateLink wiring are preserved; only the card markup is restyled (.sc).
const ICON_MAP = {
    hotel: <FaHotel size={24} />,
    apartment: <FaHome size={24} />,
    villa: <MdVilla size={24} />,
    hostel: <FaBed size={24} />,
    resort: <MdPool size={24} />,
    eco: <PiPlant size={24} />,
    guesthouse: <BsBuilding size={24} />,
    'serviced-apartment': <MdApartment size={24} />,
};

export default function StayTypesGrid({
    countryName,
    countrySlug,
    types = [],
    siteLang = 'ru',
    countryData,
    affiliateProvider,
    blockTitle,
    blockIcon,
}) {
    const isRu = siteLang === 'ru';
    const ui = t(siteLang, 'accommodationUi');
    const activeTypes = types
        .filter((t) => t.active !== false)
        .sort((a, b) => (a.order || 999) - (b.order || 999));

    if (!activeTypes || activeTypes.length === 0) return null;

    const href = buildTripCountryLinkWithMultipleFilters({
        countrySlug,
        countryName,
        siteLang,
        countryData,
        affiliateProvider,
        types: activeTypes,
        subIdSuffix: 'all-types',
    });

    const affProps = {
        href,
        provider: affiliateProvider?.code,
        vertical: 'stay',
        country: countrySlug,
        position: 'stay_type',
    };

    // Block mode — one card per block, styled exactly like the home/country
    // service band (.hm-srv2 .s): eyebrow · icon+title · included-types · CTA.
    if (blockTitle) {
        const icon =
            (blockIcon && renderTablerIcon(blockIcon, { className: 'ri' })) ||
            (blockIcon && ICON_MAP[blockIcon]) ||
            <FaHotel className="ri" />;
        const n = activeTypes.length;
        const eyebrow = ui.types.count(n);
        return (
            <AffiliateLink {...affProps} className="s" aria-label={blockTitle}>
                <span className="ix">{eyebrow}</span>
                <div className="ri-row">{icon}<h4>{blockTitle}</h4></div>
                <span className="go">{ui.types.view}</span>
            </AffiliateLink>
        );
    }

    // Legacy mode — a grid of individual type cards.
    return (
        <div className="gf-scen">
            {activeTypes.map((type, index) => {
                const title = isRu ? type.titleRu : (type.titleEn || type.titleRu);
                const icon = ICON_MAP[type.uiCode] || <FaHotel size={24} />;
                const desc = isRu ? type.descriptionRu : type.descriptionEn;
                return (
                    <AffiliateLink key={`${type.uiCode}-${type._id || index}`} {...affProps} className="sc" aria-label={title}>
                        <div className="ri-row"><span className="gf-ico">{icon}</span><h3>{title}</h3></div>
                        {desc && <p>{desc}</p>}
                        <span className="go">{ui.types.view}</span>
                    </AffiliateLink>
                );
            })}
        </div>
    );
}
